import { Link } from "react-router-dom";
import { ArrowRight, Lightbulb } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { GuideSection } from "@/pages/internal/userServicesGuideContent";

type GuideSectionBlockProps = {
  section: GuideSection;
};

export function GuideSectionBlock({ section }: GuideSectionBlockProps) {
  return (
    <section
      id={section.id}
      className="scroll-mt-24 space-y-4 border-b border-slate-100 pb-6 last:border-b-0 last:pb-0"
    >
      <div>
        <h2 className="font-heading text-lg font-semibold text-slate-900">{section.title}</h2>
        <p className="mt-2 text-sm leading-relaxed text-slate-600">{section.summary}</p>
      </div>

      {section.paragraphs?.map((paragraph, index) => (
        <p key={`${section.id}-p-${index}`} className="text-sm leading-relaxed text-slate-700">
          {paragraph}
        </p>
      ))}

      <div>
        <h3 className="text-sm font-semibold text-slate-900">What staff do</h3>
        <ul className="mt-2 list-disc space-y-1.5 pl-5 text-sm leading-relaxed text-slate-700">
          {section.staffDo.map((item, index) => (
            <li key={`${section.id}-do-${index}`}>{item}</li>
          ))}
        </ul>
      </div>

      {section.tip && (
        <div className="flex gap-2 rounded-md border border-amber-100 bg-amber-50/80 px-3 py-2.5 text-sm text-amber-950">
          <Lightbulb className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" aria-hidden />
          <p>
            <span className="font-medium">Tip: </span>
            {section.tip}
          </p>
        </div>
      )}

      {(section.cta || section.secondaryCta) && (
        <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
          {section.cta && (
            <Button asChild className="w-full sm:w-auto">
              <Link to={section.cta.to}>
                {section.cta.label}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          )}
          {section.secondaryCta && (
            <Button asChild variant="outline" className="w-full sm:w-auto">
              <Link to={section.secondaryCta.to}>{section.secondaryCta.label}</Link>
            </Button>
          )}
        </div>
      )}
    </section>
  );
}
